/**
 * Project-editor доступ к умному каталогу (задача 89 §3.2) — для карточки проекта.
 *
 * Отдельно от `api/admin.ts` (наполнение справочника, только `admin`): здесь
 * менеджеру проекта нужны лишь чтение активных видов оборудования, резолвинг
 * состава типа и чтение/замена адресного списка элементов проекта.
 */
import { api } from "./client";
import type { components } from "./types";

export type EquipmentType = components["schemas"]["EquipmentType"];
export type AssemblyElement = components["schemas"]["AssemblyElement"];
export type TypeElementsResolution = components["schemas"]["TypeElementsResolution"];
export type MetricWithOptions = components["schemas"]["MetricWithOptions"];
export type ProjectMetricValue = components["schemas"]["ProjectMetricValue"];
export type MetricValueInput = components["schemas"]["MetricValueInput"];

/** Активные виды оборудования для селектора на карточке проекта. */
export function listEquipmentTypes(): Promise<EquipmentType[]> {
  return api.get<EquipmentType[]>("/equipment-types");
}

/** Резолвинг «тип → {обязательные, опции}» (опции без дефолтов). */
export function resolveTypeElements(typeId: number): Promise<TypeElementsResolution> {
  return api.get<TypeElementsResolution>(`/equipment-types/${typeId}/elements`);
}

/** Адресный список элементов проекта. */
export function getProjectAssemblyElements(projectId: number): Promise<AssemblyElement[]> {
  return api.get<AssemblyElement[]>(`/projects/${projectId}/assembly-elements`);
}

/** Переписать адресный список элементов проекта (replace-набор). */
export function putProjectAssemblyElements(
  projectId: number,
  elementIds: number[],
): Promise<AssemblyElement[]> {
  return api.put<AssemblyElement[]>(`/projects/${projectId}/assembly-elements`, {
    element_ids: elementIds,
  });
}

/**
 * Каталог метрик по «живому» набору элементов — для опросника в drawer'е до
 * сохранения (§3.3). Пустой набор → пустой список без запроса.
 */
export function getElementsMetrics(ids: number[]): Promise<MetricWithOptions[]> {
  if (ids.length === 0) return Promise.resolve([]);
  const qs = ids.map((id) => `element_ids=${id}`).join("&");
  return api.get<MetricWithOptions[]>(`/assembly-elements/metrics?${qs}`);
}

/** Сохранённые значения метрик проекта. */
export function getProjectMetricValues(projectId: number): Promise<ProjectMetricValue[]> {
  return api.get<ProjectMetricValue[]>(`/projects/${projectId}/metric-values`);
}

/** Upsert типизированных значений метрик проекта. */
export function putProjectMetricValues(
  projectId: number,
  values: MetricValueInput[],
): Promise<ProjectMetricValue[]> {
  return api.put<ProjectMetricValue[]>(`/projects/${projectId}/metric-values`, { values });
}
